import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import { ColDef } from "ag-grid-community";
import FilterSettlementsSection from "../../components/FilterSettlementsSection";
import {
	codeFormatter,
	currencySymbol,
	priceFormatterByCurrency,
	valueDateFormatter,
} from "../../utils/formatters";
import {
	dualMoneyColumns,
	formatDualMoneyAmount,
} from "../../utils/moneyColumns";
import { CURRENCY_EUR } from "../../api/currency";
import useCurrencies from "../../hooks/useCurrencies";
import api from "../../utils/axios";
import { toast } from "react-toastify";
import { getUserRoleFromToken } from "../../utils/Auth";
import "./index.css";
import { useLocation, useNavigate } from "react-router-dom";
import PaginationTable from "../../components/Pagination";
import { Alert, Button, Divider, Input, Modal, Steps } from "antd";
import OrderDetailRow from "../../components/form/OrderDetailRow";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import {
	exportDataToCsv,
	formattedExportParams,
	moneyExportColumns,
} from "../../utils/exportDataToCsv";
import { ReactComponent as QrCodeIcon } from "../../assets/qr-code.svg";
import { settlementStatuses, userRoles } from "../../types/enums";
import { fetchSettlementDisableRequestValidation } from "../../lib/SettlementValidationApi";
import useLang from "../../hooks/useLang";
import { navigateToClientDetails } from "../../utils/navigateToClientDetails";
import { useMediaQuery } from "@mui/material";

interface ISettlementData {
	id: number;
	applicantFullName: string;
	applicantId: number;
	confirmedDate: string;
	createdAt: string;
	numberOfOrders: number;
	settlementDescription: string;
	settlementStatusDescription: string;
	settlementStatusId: number;
	sumValue: number;
	/**
	 * The EUR half of the same settlement. A settlement is partitioned by the currency each of
	 * its orders was registered in and paid out as two separate cash-register transactions, so
	 * this and `sumValue` are independent amounts and must never be added together.
	 * Zero outside PostmanAL.
	 */
	sumValueEur?: number;
}

const { TextArea } = Input;

const hexToRgba = (hex: string, alpha: number) => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
};

const Settlement = () => {
	// Right-click → Export to Excel / CSV. Without this AG-Grid 27 exports the value *before* the
	// valueFormatter, which is where every currency symbol on this page is added.
	let defaultExcelExportSettings = formattedExportParams;
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [settlementOrderTableColumns, setSettlementOrderTableColumns] =
		useState<ColDef[]>();
	const [tableData, setTableData] = useState<any>([]);
	const [open, setOpen] = useState<boolean>(false);
	const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);
	const [openSettlementOrderModal, setOpenSettlementOrderModal] =
		useState<boolean>(false);
	const [settlementDescription, setSettlementDescription] =
		useState<string>("");
	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [isClient, setIsClient] = useState<boolean>(false);
	const [settlementData, setSettlementData] = useState<ISettlementData>();
	const [settlementOrderData, setSettlementOrderData] = useState<any>();
	const [filteredSettlements, setFilteredSettlements] = useState<any>();
	const [settlementHistory, setSettlementHistory] = useState<any[]>([]);
	const [openSettlementHistory, setOpenSettlementHistory] =
		useState<boolean>();

	const [disableClientRequest, setDisableClientRequest] =
		useState<boolean>(false);
	const [openRequestModal, setOpenRequestModal] = useState<boolean>(false);
	const [settlementModalDescription, setSettlementModalDescription] =
		useState<string>("");
	const { strings } = useLang("settlements");
	const { dualCurrencyEnabled, defaultCurrencyId } = useCurrencies();
	const [filterButtonLoading, setFilterButtonLoading] =
		useState<boolean>(false);
	const location = useLocation();
	const navigate = useNavigate();

	const grid = useRef<AgGridReact | null>(null);

	let settlementOrderGridRef = useRef(null);

	const fetchTableData = async () => {
		getSettlements(
			pageSize,
			currentPage,
			filteredSettlements?.statusIds,
			filteredSettlements?.code,
			filteredSettlements?.orderDescription,
			filteredSettlements?.fromDate,
			filteredSettlements?.toDate,
			filteredSettlements?.sellerIds,
			filteredSettlements?.staffIds,
			filteredSettlements?.processedByIds
		);
	};

	const fetchSettlements = async (
		pageSize: number,
		currentPage: number,
		statusIds?: any,
		code?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		sellerIds?: any,
		staffIds?: any,
		processedByIds?: any,
		paginationDisabled?: boolean
	) => {
		const displayIdSearchTerm = code || null;
		const seller = sellerIds?.split("-")[0];

		const response = await api.post(
			"Settlement/select-settlements-paginated",
			{
				pageSize,
				currentPage,
				statusIds: statusIds ?? null,
				code: displayIdSearchTerm ?? null,
				orderDescription: orderDescription ?? null,
				fromDate: fromDate ?? null,
				toDate: toDate ?? null,
				sellerIds: parseInt(seller) ?? null,
				staffIds: parseInt(staffIds) ?? null,
				processedByIds: parseInt(processedByIds) ?? null,
				paginationDisabled,
			}
		);
		return response.data;
	};

	const fetchSettlementsCount = async (
		pageSize: number,
		currentPage: number,
		statusIds?: any,
		code?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		sellerIds?: any,
		staffIds?: any,
		processedByIds?: any,
		paginationDisabled?: boolean
	) => {
		const displayIdSearchTerm = code || null;
		const seller = sellerIds?.split("-")[0];

		const response = await api.post(
			"Settlement/select-settlements-paginated-count",
			{
				pageSize,
				currentPage,
				statusIds: statusIds ?? null,
				code: displayIdSearchTerm ?? null,
				orderDescription: orderDescription ?? null,
				fromDate: fromDate ?? null,
				toDate: toDate ?? null,
				sellerIds: parseInt(seller) ?? null,
				staffIds: parseInt(staffIds) ?? null,
				processedByIds: parseInt(processedByIds) ?? null,
				paginationDisabled,
			}
		);
		return response.data;
	};

	const fetchSettlementValidation = useCallback(async () => {
		try {
			const response = await api.get(
				"Settlement/settlement-validation-for-description",
				{ params: { documentTypeId: 9 } }
			);
			if (response.status === 200) {
				const result = response?.data;
				if (result) {
					if (
						(result.currentSettlements === 0 &&
							result.deliveredOrders === 0) ||
						(result.currentSettlements > 0 &&
							result.deliveredOrders === 0)
					) {
						setSettlementModalDescription(
							"Nuk keni Porosi te dorezuara"
						);
						// not allowed
					} else if (
						result.currentSettlementStatus ===
							settlementStatuses.DELIVERED &&
						result.currentSettlementStatus !== 0
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni barazim te ri pa e konfirmuar barazimin e kaluar"
						);
					} else if (
						result.currentSettlementStatus ===
						settlementStatuses.TRANSPORTING
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni barazim te ri. Per momentin e keni nje barazim ne transport. "
						);
					} else if (
						result.currentSettlementStatus ===
						settlementStatuses.PENDING
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni barazim te ri. Per momentin e keni nje barazim ne pritje."
						);
					} else if (
						result.currentSettlementStatus ===
						settlementStatuses.REQUESTED
					) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni barazim te ri. Per momentin e keni nje barazim te regjistruar. "
						);
					} else if (result.currentOpenReturnOrders) {
						setSettlementModalDescription(
							"Nuk mund te kerkoni Barazim te ri. Per momentin keni kthim te porosise e cila nuk eshte dorezuar ende."
						);
					}
				} else {
					setSettlementModalDescription(
						"Nuk mund te kerkoni barazim."
					);
					// not allowed
				}
			}
		} catch (error) {
			console.log(error);
		}
	}, []);

	const onRequestButtonClick = useCallback(async () => {
		try {
			const settlementRequestValidaton =
				await fetchSettlementDisableRequestValidation(9, null);

			setDisableClientRequest(settlementRequestValidaton);
			setOpenRequestModal(true);
			// }
		} catch (error: any) {
			console.log("Error: ", error?.message);
			setDisableClientRequest(true);
		}
	}, []);

	const fetchSettlementHistory = async (settlementId: string) => {
		try {
			const response = await api.get(
				"Settlement/short-settlement-history",
				{
					params: {
						settlementId: settlementId,
					},
				}
			);
			const result = response.data.reverse();
			setSettlementHistory(result);
		} catch (error) {
			console.log("Error:", error);
		}
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		fetchTableData();
		fetchSettlementValidation();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		userRoleId === userRoles.ADMIN ? setIsAdmin(true) : setIsAdmin(false);
		userRoleId === userRoles.CLIENT
			? setIsClient(true)
			: setIsClient(false);
		setTableColumns([
			{
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 38,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "id",
				headerName: strings?.code,
				valueFormatter: (params) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					fontFamily: "Inter",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 100,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementStatusDescription",
				headerName: strings?.status,
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="orderStatusDescription">
									{params.value}
								</p>
							</div>
						</div>
					);
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "uniqueSellerIdentifier",
				headerName: "Kodi unik",
				width: 100,
				suppressSizeToFit: isSmallScreen,
			},

			{
				field: "applicantUserName",
				headerName: strings?.applicant,
				width: 140,
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "createdAt",
				headerName: strings?.date,
				valueFormatter: (params) => valueDateFormatter(params?.value),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "numberOfOrders",
				headerName: strings?.orderCount,
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},
			// "Shuma LEK" and "Shuma EUR" as two columns on PostmanAL, one plain "Shuma" everywhere
			// else. A settlement is partitioned by the currency each of its orders was registered
			// in and paid out as two separate transactions, so these are independent amounts.
			...dualMoneyColumns({
				field: "sumValue",
				headerName: strings?.value,
				dualCurrencyEnabled,
				defaultCurrencyId,
				cellStyle: { fontWeight: "600" },
				width: 150,
				suppressSizeToFit: isSmallScreen,
			}),
			{
				field: "settlementDescription",
				headerName: strings?.comments,
				width: 200,
				cellStyle: {
					whiteSpace: "normal",
					overflowWrap: "break-word",
					wordWrap: "break-word",
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementProcessingDate",
				headerName: strings?.processingDate,
				width: 160,
				valueFormatter: (params) => valueDateFormatter(params?.value),
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "confirmedDate",
				headerName: strings?.confirmed,
				width: 160,
				cellRenderer: (params: any) => {
					if (
						params?.data.settlementStatusId ===
							settlementStatuses.DELIVERED &&
						userRoleId === userRoles.CLIENT
					) {
						return (
							<Button
								// icon={<CheckCircleOutlined />}
								style={{ color: "green" }}
							>
								{strings?.confirm}
							</Button>
						);
					} else if (
						params?.data.settlementStatusId ===
						settlementStatuses.CONFIRMED
					) {
						return valueDateFormatter(params?.value);
					} else {
						return "-";
					}
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "1",
				headerName: "",
				width: 190,
				cellRenderer: (params: any) => {
					if (
						params?.data.settlementStatusId >
						settlementStatuses.REQUESTED
					) {
						return (
							<>
								<Button
									// icon={<CheckCircleOutlined />}
									style={{ color: "green" }}
								>
									{strings?.open}
								</Button>
							</>
						);
					} else {
						return " - ";
					}
				},
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "deliveredBy",
				headerName: strings?.driver,
				hide: true,
			},
			{
				field: "processedBy",
				headerName: strings?.procesedBy,
				hide: true,
			},
			{
				headerName: "",
				cellRenderer: () => (
					<QrCodeIcon onClick={settlementDetailsPdf} />
				),
				suppressSizeToFit: isSmallScreen,
			},
		]);
		setSettlementOrderTableColumns([
			{
				field: "displayId",
				headerName: strings?.code,
				valueFormatter: (params: any) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					fontWeight: "400",
					lineHeight: "18px",
					color: "#4F16C2",
					textDecoration: "underline",
				}),
				width: 120,
				suppressSizeToFit: isSmallScreen,
			},

			{
				field: "orderPrice",
				headerName: strings?.orderPrice,
				// Each order inside the settlement is priced in the currency it was registered in.
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "transportPrice",
				headerName: strings?.transportPrice,
				valueFormatter: priceFormatterByCurrency("orderCurrencyId"),
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "orderDescription",
				headerName: strings?.orderDescription,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "settlementStatusDescription",
				headerName: strings?.settlementStatus,
				width: 100,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "buyer",
				headerName: strings?.buyer,
				width: 160,
				suppressSizeToFit: isSmallScreen,
			},
		]);
		// The validation request lives in the mount effect above: from here it re-ran on every
		// rebuild (up to four times per open).
		// The currency flags resolve asynchronously, so the columns must be rebuilt once they land
		// — otherwise the grid keeps the single-column layout it was first built with.
	}, [
		strings,
		isAdmin,
		setTableColumns,
		isSmallScreen,
		dualCurrencyEnabled,
		defaultCurrencyId,
	]);

	const [isSettlementRequestDisabled, setIsSettlementRequestDisabled] =
		useState<boolean>(false);

	const onSettlementRequestSend = async () => {
		setIsSettlementRequestDisabled(true);
		const params = {
			settlementDescription: settlementDescription,
		};
		try {
			const response = await api.post(
				"Settlement/requestsettlement",
				params
			);

			if (response.status === 200) {
				setSettlementDescription("");
				await fetchTableData();
				setOpenRequestModal(false);
				toast.success("Kerkesa per barazim u regjistrua me sukses!");
				// await fetchSettlementValidations();
			}
		} catch (error: any) {
			console.error(error.response.data);
		} finally {
			setIsSettlementRequestDisabled(false);
		}
	};

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const onPageChanged = (page: number, pageSize: number) => {
		setCurrentPage(page);
		getSettlements(
			pageSize,
			page,
			filteredSettlements?.statusIds,
			filteredSettlements?.code,
			filteredSettlements?.orderDescription,
			filteredSettlements?.fromDate,
			filteredSettlements?.toDate,
			filteredSettlements?.sellerIds,
			filteredSettlements?.staffIds,
			filteredSettlements?.processedByIds
		);
	};
	const handlePageSizeChange = async (current: number, size: number) => {
		setPageSize(size);
	};

	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex =
		startOrderIndex + Math.min(pageSize, total - startOrderIndex);

	const onSettlementSelected = (data: any) => {
		if (data.settlementStatusId > 1) {
			getSettlementOrders(data?.id);
			setSettlementData(data);
			setOpenSettlementOrderModal(true);
		}
	};

	const getSettlementOrders = async (settlementId: string) => {
		try {
			const response = await api.get(
				"Settlement/select-settlement-orders",
				{ params: { settlementId: settlementId } }
			);
			if (response.status === 200) {
				setSettlementOrderData(response?.data);
			}
		} catch (error) {
			console.log("Error:" + error);
		}
	};

	//here
	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "id":
				await onSettlementClicked(params?.data);
				break;
			case "1":
				await onSettlementSelected(params?.data);
				break;
			case "confirmedDate":
				await settlementConfirm(params?.data);
				break;
			case "applicantUserName":
				await navigateToClientDetails(params?.data?.applicantId);
				break;
		}
	};

	const onSettlementClicked = async (data: any) => {
		fetchSettlementHistory(data?.id);
		setSettlementData(data);
		setOpen(true);
	};

	const settlementConfirm = (data: any) => {
		if (
			data &&
			data?.settlementStatusId === settlementStatuses.DELIVERED &&
			isClient
		) {
			setSettlementData(data);
			setOpenConfirmModal(true);
		}
	};

	const transferSettlementInYourAccount = async () => {
		const data = {
			settlementId: settlementData?.id,
			settlementStatusId: settlementData?.settlementStatusId,
		};
		try {
			const response = await api.post(
				"Settlement/transfer-settlement-ownership",
				data
			);
			if (response.status === 200) {
				toast.success("Barazimi u transferua ne llogarine tuaj");
				setOpen(false);
				await fetchTableData();
				return;
			}
		} catch (error) {
			console.log("Error: ", error);
		}
	};

	const [
		isConfirmSettlementOrdersDisabled,
		setIsConfirmSettlementOrdersDisabled,
	] = useState(false);

	const confirmSettlementOrders = async () => {
		setIsConfirmSettlementOrdersDisabled(true);
		try {
			const response = await api.post(
				"Settlement/client-settlement-confirmation",
				{ settlementId: settlementData?.id }
			);
			if (response.status === 200) {
				toast.success("Success!");
				await fetchTableData();
				setOpenConfirmModal(false);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsConfirmSettlementOrdersDisabled(false);
		}
	};

	const cancelModal = () => {
		setOpenSettlementOrderModal(false);
		setSettlementOrderData(null);
	};

	const getSettlements = async (
		pageSize: number,
		currentPage: number,
		statusIds?: any[],
		code?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		sellerIds?: any[],
		staffIds?: any[],
		processedByIds?: any[],
		paginationDisabled?: boolean
	) => {
		try {
			setFilterButtonLoading(true);

			const responseData = await fetchSettlements(
				pageSize,
				currentPage,
				statusIds,
				code,
				orderDescription,
				fromDate,
				toDate,
				sellerIds,
				staffIds,
				processedByIds,
				paginationDisabled
			);
			setTableData(responseData?.data);
			const countResponseData = await fetchSettlementsCount(
				pageSize,
				currentPage,
				statusIds,
				code,
				orderDescription,
				fromDate,
				toDate,
				sellerIds,
				staffIds,
				processedByIds,
				paginationDisabled
			);
			setTotal(countResponseData?.total);
			setFilterButtonLoading(false);

			return responseData;
		} catch (err) {
			setFilterButtonLoading(false);
			console.log(err);
		}
	};

	const onFilterSettlements = (
		code: any[],
		staffIds: any[],
		statusIds: any[],
		orderDescription: any,
		fromDate: any,
		toDate: any,
		sellerIds: any[],
		processedByIds: any[]
	) => {
		setFilteredSettlements({
			statusIds,
			code,
			orderDescription,
			fromDate,
			toDate,
			sellerIds,
			staffIds,
			processedByIds,
		});
		//setCurrentPage(1);
	};

	const [selectedSellerId, setSelectedSellerId] = useState<any[]>([]);

	const onSelectionChange = () => {
		const selectedRows = (grid.current as any).api.getSelectedRows();
		const sellerIds = selectedRows?.map((item: any) => item.applicantId);
		setSelectedSellerId(sellerIds);
	};

	const openNewTab = () => {
		selectedSellerId?.forEach((id, index) => {
			const url = `/client-details?id=${id}`;
			setTimeout(() => {
				window.open(url, "_blank");
			}, 1000);
		});
	};

	const customOptions = [
		{
			label: strings?.printlist,
			value: "print_list",
		},
		{
			label: strings?.exportexcel,
			value: "export_excel",
		},
	];

	/**
	 * The sheet mirrors the grid, currency split included.
	 *
	 * `sumValue` went out as a bare number, and on PostmanAL `sumValueEur` did not go out at all —
	 * so a settlement paid partly in EUR exported as its LEK half alone, with nothing on the row
	 * to say so. Both halves are now their own column, each followed by a `Valuta` column naming
	 * the currency in words. The amount headers come from the grid, which already names them
	 * `Shuma LEK` / `Shuma EUR`.
	 */
	const selectedColumnIds = [
		"id",
		"settlementStatusDescription",
		"applicantUserName",
		"createdAt",
		"numberOfOrders",
		...moneyExportColumns({
			colId: "sumValue",
			currencyHeaderName: strings?.currencyColumn,
			currency: defaultCurrencyId,
			dualCurrencyEnabled,
			// Off PostmanAL the sheet keeps the single formatted cell it has always had.
			single: (row: any) => formatDualMoneyAmount(row?.sumValue),
		}),
		...(dualCurrencyEnabled
			? moneyExportColumns({
					colId: "sumValueEur",
					currencyHeaderName: strings?.currencyColumn,
					currency: CURRENCY_EUR,
					dualCurrencyEnabled,
			  })
			: []),
		"settlementDescription",
		"confirmedDate",
		"processedBy",
		"deliveredBy",
	];

	const onExportSelectedId = () => {
		const dataToExport = tableData.map((item: any) => ({
			// The spread goes first. It used to go last, which put every raw field back over the
			// formatted ones below it — the code went out as `123`, never as `#123`. The two
			// order-price lines that sat here went the same way and are gone: these rows are
			// settlements, and a settlement has no order price.
			...item,
			id: codeFormatter(item.id),
			sellersFullName: item.applicantUserName,
		}));
		exportDataToCsv(grid, selectedColumnIds, dataToExport);
	};

	const onFirstDataRendered = useCallback((params: any) => {
		(grid.current as any).api.sizeColumnsToFit();
	}, []);

	const settlementDetailsPdf = async () => {
		const selectedSettlementIds = grid?.current?.api
			?.getSelectedRows()
			?.map((item) => ({
				id: item.id,
			}));

		const data = {
			settlementIds: selectedSettlementIds,
			isRefusal: false,
		};

		if (!selectedSettlementIds?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		try {
			const response = await api.post(
				"Settlement/print-settlement",
				data,
				{
					responseType: "blob",
				}
			);
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(
					"Popup blocked. Please allow popups for this site."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const settlementDetailsPrintRaport = async () => {
		const selectedSettlementIds = grid?.current?.api
			?.getSelectedRows()
			?.map((item) => ({
				id: item.id,
			}));

		const data = {
			settlementIds: selectedSettlementIds,
			isRefusal: false,
		};

		if (!selectedSettlementIds?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		try {
			const response = await api.post(
				"Settlement/print-settlement-for-envelope",
				data,
				{
					responseType: "blob",
				}
			);
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				console.error(
					"Popup blocked. Please allow popups for this site."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const onModalGridCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
		}
	};
	const onOrderIdClicked = (data: any) => {
		const orderId = data?.orderId?.toString();
		// navigate("/order-details", { state: orderId });
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const settlementsLength = tableData.length;

	return (
		<>
			<div className="settlementContainer">
				<div className="settlementTable">
					<div className="settlementInfo">
						<FilterSettlementsSection
							title={strings?.filterSettlementsTitle}
							buttonTitle={strings?.filterSettlementsButton}
							showRequestButton={isClient}
							onFilterChange={onFilterSettlements}
							selectedOptions={customOptions}
							onSelectedOrderStatusChange={onExportSelectedId}
							showAdminFilters={isAdmin}
							onRequestButtonClick={onRequestButtonClick}
							onPrintTickets={settlementDetailsPrintRaport}
							settlementLength={settlementsLength}
						/>
						<div
							className="h-auto"
							style={{
								display: "flex",
								justifyContent: "flex-end",
								//paddingRight: "3.9rem",
								marginTop: "1rem",
							}}
						>
							<Button
								onClick={fetchTableData}
								disabled={filterButtonLoading}
								className="border-[#4F16C2] bg-[#4F16C2] text-white flex justify-center items-center"
								style={{ width: "100px" }}
							>
								{"Filtro"}
							</Button>
						</div>
					</div>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={total}
						startOrderIndex={startOrderIndex}
						endOrderIndex={endOrderIndex}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							height: "100%",
							width: "100%",
						}}
					>
						{isAdmin && selectedSellerId?.length > 0 && (
							<div className="flex justify-end p-4">
								<Button
									type="default"
									size="large"
									onClick={openNewTab}
									icon={<CheckOutlined />}
								>
									{strings?.openNewTabButton}
								</Button>
							</div>
						)}
						<AgGridReact
							rowData={tableData}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							defaultCsvExportParams={defaultExcelExportSettings}
							rowSelection="multiple"
							domLayout="autoHeight"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							rowHeight={60}
							onCellClicked={onCellClicked}
							onSelectionChanged={onSelectionChange}
							// onFirstDataRendered={onFirstDataRendered}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={open}
				title={`${strings?.modalTitle}${settlementData?.id}`}
				onCancel={() => setOpen(false)}
				footer={() => (
					<>
						<div className="flex items-end justify-end">
							<Button
								size="large"
								icon={<CloseOutlined />}
								onClick={() => setOpen(false)}
							>
								{strings?.closeButtonText}
							</Button>
							{isAdmin &&
								settlementData?.settlementStatusId ===
									settlementStatuses.PROCESSING && (
									<Button
										type="primary"
										size="large"
										// className="bg-blue-600"
										style={{ backgroundColor: "#4F16C2" }}
										onClick={
											transferSettlementInYourAccount
										}
										icon={<CheckOutlined />}
									>
										{strings?.transferButtonText}
									</Button>
								)}
						</div>
					</>
				)}
			>
				<Divider />
				<div>
					<OrderDetailRow
						label={strings?.orderDetailLabelCode}
						style={style.oddRow}
						value={settlementData?.id}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.orderDetailLabelDate}
						value={valueDateFormatter(
							settlementData?.createdAt ?? ""
						)}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.orderDetailLabelStatus}
						style={style.oddRow}
						value={settlementData?.settlementStatusDescription}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.orderDetailLabelClient}
						value={settlementData?.applicantFullName}
						className="driverDetailRow"
					/>
					{/* Both halves are stated separately. A settlement spanning both currencies
					    is paid out as two cash-register transactions, so there is no single total.
					    currencySymbol() with no id falls back to the deployment symbol, keeping
					    this correct on Kosovo and Macedonia. */}
					<OrderDetailRow
						label={strings?.orderDetailLabelValue}
						style={style.oddRow}
						value={`${settlementData?.sumValue.toFixed(
							2
						)} (${currencySymbol()})${
							settlementData?.sumValueEur
								? ` + ${settlementData.sumValueEur.toFixed(
										2
								  )} (${currencySymbol(CURRENCY_EUR)})`
								: ""
						}`}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.orderDetailLabelOrders}
						value={settlementData?.numberOfOrders}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label={strings?.orderDetailLabelComments}
						style={style.oddRow}
						value={settlementData?.settlementDescription}
						className="driverDetailRow"
					/>
				</div>
				<Divider />
				<div
					onClick={() =>
						setOpenSettlementHistory(!openSettlementHistory)
					}
					className="flex flex-row gap-2"
				>
					{openSettlementHistory ? <p>{"▼"}</p> : <p>{"▶"}</p>}
					<p>{strings?.historyToggleText}</p>
				</div>
				{openSettlementHistory && (
					<Steps
						progressDot
						current={10}
						direction="vertical"
						items={settlementHistory}
					/>
				)}
			</Modal>
			<Modal
				open={openConfirmModal}
				title={`${strings?.confirmModalTitle}${settlementData?.id}`}
				onCancel={() => setOpenConfirmModal(false)}
				onOk={confirmSettlementOrders}
				okButtonProps={{
					className: "bg-blue-500",
					disabled: isConfirmSettlementOrdersDisabled,
				}}
			>
				<p>
					{`${strings?.confirmText}
        ${settlementData?.id} ${strings?.confirmValueText}
        ${settlementData?.sumValue.toFixed(2)} (${currencySymbol()})${
			settlementData?.sumValueEur
				? ` + ${settlementData.sumValueEur.toFixed(
						2
				  )} (${currencySymbol(CURRENCY_EUR)})`
				: ""
		}`}
				</p>
			</Modal>
			<Modal
				open={openSettlementOrderModal}
				title={`Detajet e Barazimit me Nr.${settlementData?.id}`}
				width={1000}
				onCancel={cancelModal}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
			>
				<>
					<div>
						<Divider />
						<div
							className="ag-theme-alpine"
							style={{
								height: 600,
								width: "100%",
							}}
						>
							<AgGridReact
								rowData={settlementOrderData}
								columnDefs={settlementOrderTableColumns}
								defaultColDef={defaultColDef}
								suppressCopyRowsToClipboard={true}
								defaultExcelExportParams={
									defaultExcelExportSettings
								}
								defaultCsvExportParams={
									defaultExcelExportSettings
								}
								// domLayout="autoHeight"
								ref={settlementOrderGridRef}
								enableRangeSelection={true}
								cacheQuickFilter={true}
								rowHeight={60}
								onCellClicked={onModalGridCellClicked}
							/>
						</div>
					</div>
				</>
			</Modal>
			<Modal
				open={openRequestModal}
				title={strings?.requestModalTitle}
				onOk={() => setOpenRequestModal(false)}
				onCancel={() => setOpenRequestModal(false)}
				okButtonProps={{ className: "bg-blue-500" }}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex items-center justify-center">
							<Button
								type="primary"
								size="large"
								// className="bg-blue-600"
								style={{
									backgroundColor: "#4F16C2",
									color: "white",
								}}
								onClick={onSettlementRequestSend}
								disabled={
									disableClientRequest ||
									isSettlementRequestDisabled
								}
							>
								{strings?.registerRequestButton}
							</Button>
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-col gap-4">
					{disableClientRequest ? (
						<Alert
							type="warning"
							showIcon
							message={strings?.disableClientRequestWarning}
							description={settlementModalDescription}
						/>
					) : (
						<></>
					)}
					<TextArea
						rows={4}
						value={settlementDescription}
						onChange={(e: any) =>
							setSettlementDescription(e.target.value)
						}
					/>
				</div>
				<Divider />
			</Modal>
		</>
	);
};

export default Settlement;
const style = {
	oddRow: { backgroundColor: "#F9F8FC" },
};
